import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Phase = "initialization" | "input-mapping" | "synthesis" | "output-formation" | "completion";

export interface PhaseConfig {
  name: Phase;
  startTime: number;
  endTime: number;
  label: string;
}

const DEFAULT_PHASES: PhaseConfig[] = [
  { name: "initialization", startTime: 0, endTime: 1500, label: "Initializing" },
  { name: "input-mapping", startTime: 1500, endTime: 3500, label: "Mapping Inputs" },
  { name: "synthesis", startTime: 3500, endTime: 6000, label: "Strategic Synthesis" },
  { name: "output-formation", startTime: 6000, endTime: 8000, label: "Forming Outputs" },
  { name: "completion", startTime: 8000, endTime: Infinity, label: "Complete" },
];

const DEFAULT_STATUS_MESSAGES = [
  "Analyzing strategic context",
  "Mapping input signals",
  "Synthesizing strategic patterns",
  "Forming output architecture",
  "Finalizing results",
];

interface StrategicProcessingSequenceProps {
  title: string;
  subtitle?: string;
  isRunning: boolean;
  onComplete?: () => void;
  waitForApi?: boolean;
  phases?: PhaseConfig[];
  statusMessages?: string[];
}

function getCurrentPhase(elapsed: number, phases: PhaseConfig[]): PhaseConfig {
  for (let i = phases.length - 1; i >= 0; i--) {
    if (elapsed >= phases[i].startTime) return phases[i];
  }
  return phases[0];
}

function getPhaseProgress(elapsed: number, phase: PhaseConfig): number {
  if (phase.endTime === Infinity) return 1;
  const duration = phase.endTime - phase.startTime;
  const progress = (elapsed - phase.startTime) / duration;
  return Math.min(1, Math.max(0, progress));
}

export function StrategicProcessingSequence({
  title,
  subtitle,
  isRunning,
  onComplete,
  waitForApi = false,
  phases = DEFAULT_PHASES,
  statusMessages = DEFAULT_STATUS_MESSAGES,
}: StrategicProcessingSequenceProps) {
  const [elapsed, setElapsed] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [statusIndex, setStatusIndex] = useState(0);

  const waitForApiRef = useRef(waitForApi);
  const animationFinishedRef = useRef(false);
  const calledCompleteRef = useRef(false);

  useEffect(() => {
    waitForApiRef.current = waitForApi;
  }, [waitForApi]);

  const completionTime = useMemo(() => {
    const last = phases[phases.length - 1];
    return last.startTime;
  }, [phases]);

  const fireComplete = useCallback(() => {
    if (calledCompleteRef.current) return;
    calledCompleteRef.current = true;
    setHasCompleted(true);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (!isRunning) {
      setElapsed(0);
      setHasCompleted(false);
      setStatusIndex(0);
      animationFinishedRef.current = false;
      calledCompleteRef.current = false;
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const now = Date.now();
      const ms = now - startTime;
      setElapsed(ms);

      if (ms >= completionTime && !animationFinishedRef.current) {
        animationFinishedRef.current = true;
      }

      if (animationFinishedRef.current && !waitForApiRef.current) {
        fireComplete();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [isRunning, completionTime, fireComplete]);

  useEffect(() => {
    if (!waitForApi && animationFinishedRef.current && isRunning) {
      fireComplete();
    }
  }, [waitForApi, isRunning, fireComplete]);

  useEffect(() => {
    if (!isRunning || statusMessages.length === 0) return;
    const interval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [isRunning, statusMessages]);

  const currentPhase = getCurrentPhase(elapsed, phases);
  const phaseProgress = getPhaseProgress(elapsed, currentPhase);

  if (!isRunning) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="relative w-full rounded-md border border-primary/15 bg-gradient-to-br from-background via-card to-background overflow-hidden"
      data-testid="strategic-processing-sequence"
    >
      <DotGrid />

      <motion.div
        className="absolute top-0 left-0 h-[2px] bg-gradient-to-r from-primary/60 via-primary to-primary/60"
        initial={{ width: "0%" }}
        animate={{ width: currentPhase.name === "completion" ? "100%" : `${Math.min(95, (elapsed / completionTime) * 100)}%` }}
        transition={{ duration: 0.3 }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-[380px] p-8">
        <AnimatePresence mode="wait">
          {currentPhase.name === "initialization" && (
            <InitializationPhase
              key="init"
              title={title}
              subtitle={subtitle}
              progress={phaseProgress}
            />
          )}
          {currentPhase.name === "input-mapping" && (
            <InputMappingPhase
              key="input"
              progress={phaseProgress}
            />
          )}
          {currentPhase.name === "synthesis" && (
            <SynthesisPhase
              key="synth"
              progress={phaseProgress}
            />
          )}
          {currentPhase.name === "output-formation" && (
            <OutputFormationPhase
              key="output"
              progress={phaseProgress}
            />
          )}
          {currentPhase.name === "completion" && (
            <CompletionPhase
              key="complete"
              title={title}
              statusMessage={statusMessages[statusIndex]}
              isWaiting={waitForApi}
            />
          )}
        </AnimatePresence>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
          <AnimatePresence mode="wait">
            <motion.span
              key={statusIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.6, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="text-[10px] text-muted-foreground/60 italic text-center"
            >
              {statusMessages[statusIndex]}
            </motion.span>
          </AnimatePresence>
          <PhaseIndicator currentPhase={currentPhase.name} phases={phases} />
        </div>
      </div>
    </motion.div>
  );
}

function DotGrid() {
  return (
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
        backgroundSize: "20px 20px",
      }}
    />
  );
}

function PhaseIndicator({ currentPhase, phases }: { currentPhase: Phase; phases: PhaseConfig[] }) {
  const phaseIndex = phases.findIndex(p => p.name === currentPhase);

  return (
    <div className="flex items-center gap-2" data-testid="phase-indicator">
      {phases.map((phase, i) => {
        const isActive = i === phaseIndex;
        const isComplete = i < phaseIndex;

        return (
          <div key={phase.name} className="flex items-center gap-2">
            <motion.div
              className={`rounded-full ${
                isActive
                  ? "h-2 w-2 bg-primary shadow-[0_0_8px_rgba(124,58,237,0.5)]"
                  : isComplete
                  ? "h-1.5 w-1.5 bg-primary/40"
                  : "h-1.5 w-1.5 bg-muted-foreground/20"
              }`}
              animate={isActive ? { scale: [1, 1.3, 1] } : {}}
              transition={isActive ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" } : {}}
            />
            {i < phases.length - 1 && (
              <div
                className={`h-px w-4 ${
                  isComplete ? "bg-primary/30" : "bg-muted-foreground/10"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function InitializationPhase({
  title,
  subtitle,
  progress,
}: {
  title: string;
  subtitle?: string;
  progress: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center text-center"
      data-testid="phase-initialization"
    >
      <motion.div
        className="relative mb-6"
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      >
        <div className="h-16 w-16 rounded-full border-2 border-primary/20 flex items-center justify-center">
          <motion.div
            className="h-8 w-8 rounded-full bg-primary/10 border border-primary/30"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <motion.div
          className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </motion.div>

      <h3
        className="text-lg font-bold tracking-tight mb-1"
        data-testid="text-processing-title"
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm text-muted-foreground/70 max-w-sm" data-testid="text-processing-subtitle">
          {subtitle}
        </p>
      )}

      <motion.div
        className="mt-4 h-1 w-32 rounded-full bg-muted/30 overflow-hidden"
        data-testid="progress-bar-init"
      >
        <motion.div
          className="h-full bg-primary/40 rounded-full"
          initial={{ width: "0%" }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.1 }}
        />
      </motion.div>
    </motion.div>
  );
}

function InputMappingPhase({ progress }: { progress: number }) {
  const inputCards = useMemo(
    () => [
      { label: "Product", delay: 0, x: -60, y: -40 },
      { label: "Persona", delay: 0.3, x: 60, y: -40 },
      { label: "Narrative Opportunity", delay: 0.6, x: -60, y: 40 },
      { label: "Selected Angles", delay: 0.9, x: 60, y: 40 },
    ],
    []
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4 }}
      className="relative flex items-center justify-center w-full h-[260px]"
      data-testid="phase-input-mapping"
    >
      <motion.div
        className="absolute h-12 w-12 rounded-full border-2 border-primary/30 flex items-center justify-center"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="h-4 w-4 rounded-full bg-primary/20" />
      </motion.div>

      {inputCards.map((card, i) => {
        const visible = progress > i * 0.25;
        return (
          <motion.div
            key={card.label}
            className="absolute"
            initial={{ opacity: 0, x: card.x * 1.5, y: card.y * 1.5 }}
            animate={
              visible
                ? { opacity: 1, x: card.x, y: card.y }
                : { opacity: 0, x: card.x * 1.5, y: card.y * 1.5 }
            }
            transition={{ delay: card.delay, duration: 0.6, ease: "easeOut" }}
          >
            <div className="px-3 py-2 rounded-md border border-primary/15 bg-card text-[11px] font-medium text-foreground/70 whitespace-nowrap">
              {card.label}
            </div>
            {visible && (
              <GlowingLine
                fromX={card.x > 0 ? -20 : 20}
                fromY={card.y > 0 ? -10 : 10}
                delay={card.delay + 0.3}
              />
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
}

function GlowingLine({
  fromX,
  fromY,
  delay,
}: {
  fromX: number;
  fromY: number;
  delay: number;
}) {
  return (
    <motion.div
      className="absolute top-1/2 left-1/2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.4 }}
      transition={{ delay, duration: 0.5 }}
    >
      <svg
        width={Math.abs(fromX) + 4}
        height={Math.abs(fromY) + 4}
        className="overflow-visible"
        style={{
          position: "absolute",
          top: fromY < 0 ? fromY : 0,
          left: fromX < 0 ? fromX : 0,
        }}
      >
        <motion.line
          x1={fromX < 0 ? 0 : Math.abs(fromX)}
          y1={fromY < 0 ? 0 : Math.abs(fromY)}
          x2={fromX < 0 ? Math.abs(fromX) : 0}
          y2={fromY < 0 ? Math.abs(fromY) : 0}
          stroke="hsl(262, 83%, 58%)"
          strokeWidth="1.5"
          strokeOpacity="0.3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay, duration: 0.6 }}
        />
      </svg>
    </motion.div>
  );
}

function SynthesisPhase({ progress }: { progress: number }) {
  const branches = useMemo(
    () => [
      { label: "Tension", delay: 0.1 },
      { label: "Desire", delay: 0.3 },
      { label: "Proof", delay: 0.5 },
      { label: "Friction", delay: 0.7 },
      { label: "Brand Role", delay: 0.9 },
      { label: "Angle Fit", delay: 1.1 },
    ],
    []
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4 }}
      className="relative flex flex-col items-center w-full"
      data-testid="phase-synthesis"
    >
      <motion.div
        className="relative mb-8"
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.div
          className="h-20 w-20 rounded-full border-2 border-primary/30 flex items-center justify-center"
          animate={{ boxShadow: ["0 0 0px rgba(124,58,237,0)", "0 0 24px rgba(124,58,237,0.2)", "0 0 0px rgba(124,58,237,0)"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div
            className="h-10 w-10 rounded-full bg-primary/15 border border-primary/25"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>

      <div className="flex flex-wrap gap-2 justify-center max-w-[320px]">
        {branches.map((branch) => {
          const visible = progress > branch.delay / 1.5;
          return (
            <motion.div
              key={branch.label}
              initial={{ opacity: 0, y: 12 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: branch.delay, duration: 0.4 }}
              className="px-3 py-1.5 rounded-full border border-primary/15 bg-primary/[0.04] text-[10px] font-medium text-foreground/60"
            >
              {branch.label}
            </motion.div>
          );
        })}
      </div>

      <motion.div
        className="mt-6 flex items-center gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: progress > 0.5 ? 1 : 0 }}
        transition={{ duration: 0.5 }}
      >
        <span className="text-[10px] text-muted-foreground/50 italic">Synthesizing strategic patterns</span>
        <AnimatedDots />
      </motion.div>
    </motion.div>
  );
}

function OutputFormationPhase({ progress }: { progress: number }) {
  const stages = useMemo(
    () => [
      { label: "Awareness", group: "Cognitive", delay: 0 },
      { label: "Knowledge", group: "Cognitive", delay: 0.2 },
      { label: "Liking", group: "Affective", delay: 0.4 },
      { label: "Preference", group: "Affective", delay: 0.6 },
      { label: "Conviction", group: "Affective", delay: 0.8 },
      { label: "Purchase", group: "Behavioral", delay: 1.0 },
    ],
    []
  );

  const GROUP_COLORS: Record<string, string> = {
    Cognitive: "border-blue-500/20 bg-blue-500/5",
    Affective: "border-pink-500/20 bg-pink-500/5",
    Behavioral: "border-amber-500/20 bg-amber-500/5",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-[320px] space-y-2"
      data-testid="phase-output-formation"
    >
      {stages.map((stage) => {
        const visible = progress > stage.delay / 1.2;
        const colorClass = GROUP_COLORS[stage.group] || "";
        return (
          <motion.div
            key={stage.label}
            initial={{ opacity: 0, x: -16, filter: "blur(4px)" }}
            animate={visible ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ delay: stage.delay, duration: 0.5 }}
            className={`rounded-md border p-3 ${colorClass}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-foreground/70">
                {stage.label}
              </span>
              <span className="text-[9px] text-muted-foreground/50">{stage.group}</span>
            </div>
            <motion.div
              className="mt-1.5 h-1 w-full bg-muted/20 rounded-full overflow-hidden"
            >
              <motion.div
                className="h-full bg-primary/30 rounded-full"
                initial={{ width: "0%" }}
                animate={visible ? { width: "100%" } : {}}
                transition={{ delay: stage.delay + 0.3, duration: 0.8, ease: "easeOut" }}
              />
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

function CompletionPhase({ title, statusMessage, isWaiting }: { title: string; statusMessage?: string; isWaiting?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center text-center"
      data-testid="phase-completion"
    >
      <motion.div className="relative mb-5">
        <div className="h-16 w-16 rounded-full border border-primary/20 flex items-center justify-center">
          <motion.div
            className="h-8 w-8 rounded-full bg-primary/10"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        {[0, 1, 2, 3].map((i) => {
          const angle = (i / 4) * Math.PI * 2;
          return (
            <motion.div
              key={i}
              className="absolute h-1.5 w-1.5 rounded-full bg-primary/30"
              style={{
                top: `calc(50% + ${Math.sin(angle) * 36}px - 3px)`,
                left: `calc(50% + ${Math.cos(angle) * 36}px - 3px)`,
              }}
              animate={{ opacity: [0.2, 0.7, 0.2] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
            />
          );
        })}
        {isWaiting && (
          <motion.div
            className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full bg-primary"
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
      </motion.div>

      <h3 className="text-sm font-semibold tracking-tight text-foreground/80 mb-1">
        {title}
      </h3>
      <p className="text-[11px] text-muted-foreground/50">
        {isWaiting ? "Waiting for strategy response..." : "Processing complete"}
      </p>
    </motion.div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex gap-0.5 ml-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          className="h-0.5 w-0.5 rounded-full bg-muted-foreground/40 inline-block"
        />
      ))}
    </span>
  );
}
